import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import SearchBar from '@src/components/search-bar/search-bar.jsx';
import ElementService from '@src/js/services/element-service.js';
import SearchApiService from '@src/js/services/search-api-service.js';
import SearchResultsCountCard from '@src/components/search-results-count-card/search-results-count-card.jsx';
import SearchResult from '@src/components/search-results/search-result.jsx';
import SearchResultService from '@src/js/services/search-result-service.js';
import BackToTopBlock from '@js/back-to-top.es6.js';
import Mark from 'mark.js'
import {
  FILTER_IDS,
  SearchApiResultsConstants as SearchResultsConstants,
  ZERO_INDEX_EQUALIZER,
} from './constants.js';
import NoResultsContainer from '@src/js/no-results-container.js';
import { Pagination } from '@src/components/core/core-component-imports.js';
import GlobalFilterList from '@src/components/search-results/global-filter-list.jsx';
import SearchResultsPageContentType from '@js/content-types/search-results-page-content-type.es6.js';

export default class SearchResultsContainer {
  constructor() {
    this.elementService = new ElementService();
    this.searchApiService = new SearchApiService();
    this.searchResultService = new SearchResultService();

    this.backToTopBlock = new BackToTopBlock();
    this.noResultsContainer = new NoResultsContainer();

    const searchResultsPage = new SearchResultsPageContentType();
    this.placeholderText = searchResultsPage.searchPlaceholder.item.$(0).text();
    this.searchButtonText = searchResultsPage.searchButtonText.item.$(0).text();
    this.pageLabel = searchResultsPage.pageLabel.item.$(0).text();
    this.forLabel = searchResultsPage.forLabel.item.$(0).text();
    this.resultsLabel = searchResultsPage.resultsLabel.item.$(0).text();
    this.ofLabel = searchResultsPage.ofLabel.item.$(0).text();
    this.providersLabel = searchResultsPage.providersResultsLabel.item.$(0).text();
    this.locationsLabel = searchResultsPage.locationsResultsLabel.item.$(0).text();

    this.viewAllProvidersUrl = searchResultsPage.viewAllProvidersLink.anchor.$(0).attr('href');
    this.viewAllProvidersLabel = searchResultsPage.viewAllProvidersLink.anchor.$(0).text();

    this.viewAllLocationsUrl = searchResultsPage.viewAllLocationsLink.anchor.$(0).attr('href');
    this.viewAllLocationsLabel = searchResultsPage.viewAllLocationsLink.anchor.$(0).text();

    this.searchBar = null;
    this.clearButton = null;

    this.searchResultsPageWrapper = document.querySelector('.search-results-page--wrapper');
    this.searchResultsTopLevelWrappers = this.searchResultsPageWrapper.querySelectorAll('.search-results-top-level--wrapper');

    this.genericCard = this.searchResultsPageWrapper.querySelector('.field--name-field-generic-card');
    this.searchResultsContainers = this.elementService.getChildElementsByClass(this.searchResultsTopLevelWrappers, '.search-results--container');
    this.providerResultsWrappers = this.elementService.getChildElementsByClass(this.searchResultsTopLevelWrappers, '.provider-results-card--wrapper');
    this.locationResultsWrappers = this.elementService.getChildElementsByClass(this.searchResultsTopLevelWrappers, '.location-results-card--wrapper');
    this.loadingBoxes = this.elementService.getChildElementsByClass(this.searchResultsTopLevelWrappers, '.loading-box');

    this.resultsForText = this.searchResultsPageWrapper.querySelector('.results-for-text');
    this.paginationText = this.searchResultsPageWrapper.querySelector('.pagination-text');
    this.resultsForAndCountWrapper = this.searchResultsPageWrapper.querySelector('.results-for-count--wrapper');
    this.paginationContainer = this.searchResultsPageWrapper.querySelector('.pagination--container');
    this.filtersContainer = this.searchResultsPageWrapper.querySelector('.search-filters--container');
    this.hide_blog_categories = true;
    this.startingResNum = 1;
    this.endingResNum = null;
    this.totalNumOfResults = null;
    this.searchedText = null;
    this.startingPageNum = 0;
    this.itemsPerPage = null;
    this.totalPages = null;
    this.zeroIndexEqualizer = 1;
    this.resultsCount = {};

    this.onSubmit = this.onSubmit.bind(this);
    this.onFormReset = this.onFormReset.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.onFilterSelected = this.onFilterSelected.bind(this);
    this.getResults = this.getResults.bind(this);

    this.markInstance = null;
  }

  async setFilteredResultsCounts() {
    this.resultsCount = {
      allResultsCount: (await this.searchApiService.searchAllWithPageNum(this.searchedText, 0)).pager[SearchResultsConstants.TOTAL_ITEMS],
      conditionResultsCount: (await this.searchApiService.searchConditionPages(this.searchedText, 0)).pager[SearchResultsConstants.TOTAL_ITEMS],
      leadershipResultsCount: (await this.searchApiService.searchLeadership(this.searchedText, 0)).pager[SearchResultsConstants.TOTAL_ITEMS],
      locationsResultsCount: (await this.searchApiService.searchLuminisLocations(this.searchedText, 0)).pager[SearchResultsConstants.TOTAL_ITEMS],
      providersResultsCount: (await this.searchApiService.searchProviders({ searchedText: this.searchedText, pageNum: 0 })).pager[SearchResultsConstants.TOTAL_ITEMS],
      researchResultsCount: (await this.searchApiService.searchResearchPages(this.searchedText, 0)).pager[SearchResultsConstants.TOTAL_ITEMS],
      serviceResultsCount: (await this.searchApiService.searchServicePages(this.searchedText, 0)).pager[SearchResultsConstants.TOTAL_ITEMS],
      specialtyResultsCount: (await this.searchApiService.searchSpecialtyPages(this.searchedText, 0)).pager[SearchResultsConstants.TOTAL_ITEMS],
      treatmentResultsCount: (await this.searchApiService.searchTreatmentPages(this.searchedText, 0)).pager[SearchResultsConstants.TOTAL_ITEMS],
      blogResultsCount: (await this.searchApiService.searchBlogPages(this.searchedText, 0)).pager[SearchResultsConstants.TOTAL_ITEMS],
    }
  }

  markText() {
    this.markInstance = new Mark(this.searchResultsPageWrapper.querySelectorAll('.body'));
    this.markInstance.mark(this.searchedText);
  }

  setEndingResNum(pageNum) {
    if (pageNum === 0) {
      if (this.totalNumOfResults < this.itemsPerPage) {
        this.endingResNum = this.totalNumOfResults;
      } else {
        this.endingResNum = this.itemsPerPage;
      }
    } else if (pageNum < this.totalPages - this.zeroIndexEqualizer) {
      this.endingResNum = (pageNum + this.zeroIndexEqualizer) * this.itemsPerPage;
    }
    else {
      this.endingResNum = this.totalNumOfResults;
    }
  }

  setPaginationText(pageNum) {
    this.setEndingResNum(pageNum);
    const startToEndDifference = this.itemsPerPage - 1;

    this.startingResNum = this.itemsPerPage < this.totalNumOfResults ? this.endingResNum - startToEndDifference : 1;
    this.paginationText.innerText = `${this.startingResNum}-${this.endingResNum} ${this.ofLabel} ${this.totalNumOfResults}`;
  }

  setResultsForText() {
    this.resultsForText.innerText = `${this.resultsLabel} ${this.forLabel} "${this.searchedText}"`;
  }

  onInputChange() {
    this.elementService.toggleVisibleStateOfElement(this.clearButton, true);
  }

  onFormReset() {
    this.elementService.toggleVisibleStateOfElement(this.clearButton, false);
  }

  async onFilterSelected() {
    const url = new URL(window.location);
    const pageNumOneBased = url.searchParams.get(FILTER_IDS.PAGE);
    const pageNumZeroBased = pageNumOneBased - ZERO_INDEX_EQUALIZER;
    await this.getResults(pageNumZeroBased);
  }

  showLoadingResults() {
    this.elementService.toggleVisibleStateOfAllElementsInArray(this.loadingBoxes, true);
    this.elementService.toggleVisibleStateOfElement(this.genericCard, true);
    this.elementService.toggleVisibleStateOfAllElementsInArray(this.providerResultsWrappers, false);
    this.elementService.toggleVisibleStateOfAllElementsInArray(this.locationResultsWrappers, false);
    this.elementService.toggleVisibleStateOfAllElementsInArray(this.searchResultsContainers, false);
    this.elementService.toggleVisibleStateOfElement(this.resultsForAndCountWrapper, false);
    this.elementService.toggleVisibleStateOfElement(this.paginationContainer, false);
    this.elementService.toggleVisibleStateOfElement(this.filtersContainer, false);
    this.noResultsContainer.toggleContainerDisplay(false);
  }

  showResults(pageNum, results) {
    this.setResultsForText();
    this.totalNumOfResults = results.pager[SearchResultsConstants.TOTAL_ITEMS];
    this.setPaginationText(pageNum)
    this.elementService.toggleVisibleStateOfAllElementsInArray(this.loadingBoxes, false);

    if (pageNum === this.startingPageNum) {
      if (this.resultsCount.providersResultsCount > 0) {
        this.showProviderResults();
      }
      if (this.resultsCount.locationsResultsCount > 0) {
        this.showLocationResults();
      }
    }

    ReactDOM.render(
      <BrowserRouter>
          <GlobalFilterList
            selectionHandler={this.onFilterSelected}
            allResultsCount={this.resultsCount.allResultsCount}
            conditionResultsCount={this.resultsCount.conditionResultsCount}
            leadershipResultsCount={this.resultsCount.leadershipResultsCount}
            locationResultsCount={this.resultsCount.locationsResultsCount}
            providerResultsCount={this.resultsCount.providersResultsCount}
            researchResultsCount={this.resultsCount.researchResultsCount}
            serviceResultsCount={this.resultsCount.serviceResultsCount}
            specialtyResultsCount={this.resultsCount.specialtyResultsCount}
            treatmentResultsCount={this.resultsCount.treatmentResultsCount}
            blogResultsCount={this.resultsCount.blogResultsCount}
            hideBlogCategoryFilter={this.hide_blog_categories}
          />
      </BrowserRouter>,
      this.filtersContainer
    );

    this.showResultsList(results.rows);
    this.initPagination();
    this.markText();
  }

  buildProviderResultsText() {
    return `<strong>${this.resultsCount.providersResultsCount} ${this.providersLabel}</strong> ${this.forLabel} "${this.searchedText}"`;
  }

  buildLocationResultsText() {
    return `<strong>${this.resultsCount.locationsResultsCount} ${this.locationsLabel}</strong> ${this.forLabel} "${this.searchedText}"`;
  }

  showProviderResults() {
    const providerResultsContainers = this.elementService.getChildElementsByClass(this.searchResultsTopLevelWrappers, '.providers-results-count-card--container');
    this.elementService.toggleVisibleStateOfAllElementsInArray(this.providerResultsWrappers, true);

    providerResultsContainers.forEach(container => {
      ReactDOM.render(
        <>
          <SearchResultsCountCard
            key={'provider-results-count-card'}
            resultsText={this.buildProviderResultsText()}
            resultsLinkText={this.viewAllProvidersLabel}
            resultsLink={this.viewAllProvidersUrl}
          />
        </>,
        container
      );
    });

    const cards = document.querySelectorAll('.search-results-count-card');
    cards.forEach(card => {
      card.addEventListener('click', event => {
        const cardUrl = new URL(card.href);
        cardUrl.searchParams.set(
          FILTER_IDS.SEARCH,
          this.searchedText,
        );
        card.href = cardUrl;
        return true;
      }
      );
    });
  }

  showLocationResults() {
    const locationResultsContainers = this.elementService.getChildElementsByClass(this.searchResultsTopLevelWrappers, '.locations-results-count-card--container');
    this.elementService.toggleVisibleStateOfAllElementsInArray(this.locationResultsWrappers, true);

    locationResultsContainers.forEach(container => {
      const resultsLinkURL = new URL(window.location.origin + this.viewAllLocationsUrl);
      resultsLinkURL.searchParams.set(
        FILTER_IDS.SEARCH,
        this.searchedText
      );

      ReactDOM.render(
        <>
          <SearchResultsCountCard
            key={'locations-results-count-card'}
            resultsText={this.buildLocationResultsText()}
            resultsLinkText={this.viewAllLocationsLabel}
            resultsLink={resultsLinkURL}
          />
        </>,
        container
      );
    })
  }

  showResultsList(results) {
    this.elementService.toggleVisibleStateOfAllElementsInArray(this.searchResultsContainers, true);
    this.elementService.toggleVisibleStateOfElement(this.resultsForAndCountWrapper, true);
    this.elementService.toggleVisibleStateOfElement(this.filtersContainer, true);

    this.searchResultsContainers.forEach(container => {
      ReactDOM.render(
        <>
          {results.map((result, index) => {
            return (
              <SearchResult
                key={`search-result-${index}`}
                type={this.searchResultService.getResultType(result)}
                nodeLink={this.searchResultService.getNodeLink(result)}
                resultText={this.searchResultService.getResultBody(result)}
              />
            );
          })}
        </>,
        container
      );
    })
  }

  setPageInfo(results) {
    this.itemsPerPage = results.pager[SearchResultsConstants.ITEMS_PER_PAGE];
    this.totalPages = results.pager[SearchResultsConstants.TOTAL_PAGES];
  }

  showNoResults() {
    this.elementService.toggleVisibleStateOfElement(this.genericCard, false);
    this.elementService.toggleVisibleStateOfAllElementsInArray(this.loadingBoxes, false);
    this.noResultsContainer.updateContainer(this.searchedText);
    this.noResultsContainer.toggleContainerDisplay(true);
  }

  async getResults(pageNum = 0) {
    this.showLoadingResults();

    const url = new URL(window.location);
    const category = url.searchParams.get(FILTER_IDS.CATEGORY);
    // only show blog category filter if we're filtering by blogs
    this.hide_blog_categories = category !== FILTER_IDS.BLOG;
    // Set blog category to category param
    let blogCategory = this.hide_blog_categories ? "" : url.searchParams.get(FILTER_IDS.BLOG_CATEGORY);
    const results = await this.searchApiService.getSearchWithFilter(this.searchedText, category, blogCategory, pageNum);

    if (results.rows.length > 0) {
      await this.setFilteredResultsCounts();
      this.setPageInfo(results);
      this.showResults(pageNum, results);
    } else {
      this.showNoResults();
    }
    this.backToTopBlock.setVisualState();
  }

  async onSubmit(event) {
    event.preventDefault();
    this.searchedText = event.target[0].value;

    const url = new URL(window.location);
    url.searchParams.set(FILTER_IDS.SEARCH, this.searchedText);

    const category = url.searchParams.get(FILTER_IDS.CATEGORY);
    if (!category) {
      url.searchParams.set(FILTER_IDS.CATEGORY, FILTER_IDS.ALL_RESULTS);
    }

    const pageNum = url.searchParams.get(FILTER_IDS.PAGE);
    if (pageNum) {
      this.startingPageNum = pageNum - ZERO_INDEX_EQUALIZER;
    }
    else {
      this.startingPageNum = 0;
      const startingPageNumOneBased = this.startingPageNum + ZERO_INDEX_EQUALIZER;
      url.searchParams.set(FILTER_IDS.PAGE, startingPageNumOneBased);
    }

    window.history.pushState({}, '', decodeURIComponent(url));
    await this.getResults(this.startingPageNum);
  }

  setAndGetQueryFromUrl() {
    const url = new URL(window.location);
    this.searchBar.value = url.searchParams.get(FILTER_IDS.SEARCH);
    this.searchResultsPageWrapper.querySelector('.search-bar--form')
      .querySelector('.submit-button').click();
  }

  initPagination() {
    this.elementService.toggleVisibleStateOfElement(this.paginationContainer, true);

    ReactDOM.render(
      <BrowserRouter>
        <Pagination
          pageLabel={this.pageLabel}
          ofLabel={this.ofLabel}
          totalPages={this.totalPages}
          paginationHandler={this.getResults}
        />
      </BrowserRouter>,
      this.paginationContainer
    );
  }

  async initContainer() {
    ReactDOM.render(
      <>
        <SearchBar
          key={'search-results-search-bar'}
          placeholderText={this.placeholderText}
          searchButtonText={this.searchButtonText}
          onSubmit={this.onSubmit}
          onFormReset={this.onFormReset}
          onInputChange={this.onInputChange}
        />
      </>,
      this.searchResultsPageWrapper.querySelector('.search-bar--container')
    );
    this.clearButton = this.searchResultsPageWrapper.querySelector('.clear-button--label');
    this.searchBar = this.searchResultsPageWrapper.querySelector('.site-search-search-bar');
    this.setAndGetQueryFromUrl();
    this.elementService.toggleVisibleStateOfElement(this.clearButton, true);
  }
}

(async () => {
  if (document.querySelector(
    '.search-results-page--wrapper',
  )) {
    const searchResultsContainer = new SearchResultsContainer();
    await searchResultsContainer.initContainer();
  }
})(Drupal);
